import { useState, useEffect } from 'react';

interface SplashScreenProps {
  onComplete: () => void;
  duration?: number; // Duration in milliseconds (default 2500ms)
  skipAfter?: number; // Allow skip after this many milliseconds (default 1000ms)
}

export const SplashScreen = ({ 
  onComplete, 
  duration = 2500,
  skipAfter = 1000 
}: SplashScreenProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [canSkip, setCanSkip] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = () => {
    setIsExiting(true);
    // Wait for fade-out animation to complete
    setTimeout(() => {
      setIsVisible(false);
      onComplete();
    }, 500); // Match fade-out animation duration
  };

  useEffect(() => {
    // Prevent scrolling while splash is visible
    document.body.style.overflow = 'hidden';

    // Allow skip after specified time
    const skipTimer = setTimeout(() => {
      setCanSkip(true);
    }, skipAfter);

    // Auto-dismiss after duration
    const dismissTimer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => {
      clearTimeout(skipTimer);
      clearTimeout(dismissTimer);
      // Restore scrolling when component unmounts
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, skipAfter]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-brand-beige transition-opacity duration-500 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
      aria-label="Loading splash screen"
    >
      {/* Logo */}
      <div className={`animate-fade-in-up ${isExiting ? 'animate-fade-out' : ''}`}>
        <img
          src="/img/logo/thepearl-1.svg"
          alt="The Pearl at Grand Harbour"
          className="w-full max-w-md mx-auto drop-shadow-2xl scale-[3.5]"
        />
      </div>

      {/* Skip Button - appears after skipAfter time */}
      {canSkip && !isExiting && (
        <button
          onClick={handleDismiss}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-brand-navy/60 hover:text-brand-navy/90 text-sm font-medium transition-colors duration-300 underline"
          aria-label="Skip splash screen"
        >
          Skip
        </button>
      )}
    </div>
  );
};

